import { formatMinutesToHumanReadable } from "@Src/utils/time";
import { Box, Button, Paper, Typography } from "@mui/material";
import { type ReactNode, useState } from "react";
import { useRecordContext } from "react-admin";

interface FormIntroWrapperProps {
	/** The content to render after Next is clicked */
	children: ReactNode;
}

export const FormIntroWrapper = ({ children }: FormIntroWrapperProps) => {
	const [step, setStep] = useState(0);
	const record = useRecordContext();

	// Determine if disclaimer exists
	const hasDisclaimer = Boolean(record?.["initial_disclaimer"]);

	// Calculate total steps: Step 0 (always), Step 1 (disclaimer - conditional), Final step (children)
	const finalStep = hasDisclaimer ? 2 : 1;

	const handleNext = () => {
		setStep((prevStep) => prevStep + 1);
	};

	// Step 0: Max allowed time + "PLEASE READ CAREFULLY" warning
	if (step === 0) {
		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#fafafa",
					p: 2,
				}}
			>
				<Paper
					elevation={3}
					sx={{
						p: 4,
						maxWidth: 650,
						textAlign: "left",
						borderRadius: 3,
					}}
				>
					{record?.["max_allowed_time"] && (
						<Typography variant="h6" color="primary" sx={{ mb: 2 }}>
							Max allowed time:{" "}
							{formatMinutesToHumanReadable(record["max_allowed_time"])}
						</Typography>
					)}
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						<strong style={{ color: "red" }}>PLEASE READ CAREFULLY</strong>
						<br />
						<strong>
							- Once you start the form, you cannot stop, and you must finish in
							one go.
						</strong>
						<br />
						<strong>
							- You will receive an email with single-use link. Only click the
							link when you are ready to go through the form.
						</strong>
						<br />
						<br />
						If you already have submitted your email for this form, no new email
						will be sent (spam protection).
					</Typography>

					<Box textAlign="center">
						<Button
							variant="contained"
							color="primary"
							size="large"
							onClick={handleNext}
						>
							Next
						</Button>
					</Box>
				</Paper>
			</Box>
		);
	}

	// Step 1: Initial disclaimer (only if it exists)
	if (step === 1 && hasDisclaimer) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#fafafa",
					p: 2,
					overflowY: "auto",
				}}
			>
				<Paper
					elevation={3}
					sx={{
						p: 4,
						maxWidth: 650,
						textAlign: "left",
						borderRadius: 3,
						my: 2,
					}}
				>
					<Box
						sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
					>
						<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
							{record?.["initial_disclaimer"]}
						</Typography>
					</Box>

					<Box textAlign="center">
						<Button
							variant="contained"
							color="primary"
							size="large"
							onClick={handleNext}
						>
							Next
						</Button>
					</Box>
				</Paper>
			</Box>
		);
	}

	// Final step: Render children (email form)
	if (step >= finalStep) {
		return <Box>{children}</Box>;
	}

	// Fallback (should never reach here with proper logic)
	return <Box>{children}</Box>;
};
